import { Component, ViewEncapsulation, ViewChild, OnInit, AfterViewInit, OnDestroy, DestroyRef, inject, ElementRef, CUSTOM_ELEMENTS_SCHEMA, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, UntypedFormGroup, ReactiveFormsModule } from '@angular/forms';
import { take } from 'rxjs/operators';
import { MatCheckboxModule, MatCheckboxChange } from '@angular/material/checkbox';
import { MatMenuModule } from '@angular/material/menu';
import { MatIconModule } from '@angular/material/icon';
import { IonicModule } from '@ionic/angular';
import { ChipFilter } from 'src/models/schedule.model';
import { DateTime as LuxonDateTime } from 'luxon';
import { MatTableDataSource } from '@angular/material/table';

import { Client } from 'src/providers/client/client';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { Invoices } from 'src/providers/invoices/invoices';
import { Pros } from 'src/providers/pros/pros';
import { WindowService } from 'src/shared/providers/window.service';
import { ChatDockService } from 'src/shared/services/chat-dock.service';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';

import { Util } from 'src/shared/util/util';

import { Loading } from 'src/shared/components/loading/loading';

import { InvoiceFilterParams } from 'src/models/invoice.model';

import { TidyCurrencyPipe } from 'src/shared/pipes/tidy-currency.pipe';
import { TidyAbbreviateLastName } from 'src/shared/pipes/tidy-abbr-lastname.pipe';
import { ParseDateTimePipe } from 'src/shared/pipes/parse-date.pipe';
import { getSellerDisplay } from 'src/shared/util/get-seller-display';
import { SharedModule } from 'src/shared/shared.module';

import { BillSettingsPage } from 'src/pages/bills/bills-settings/bills-settings';
import { PaidInvoicePage } from 'src/pages/invoices/paid-invoice/paid-invoice';
import { PendingInvoicePage } from 'src/pages/invoices/pending-invoice/pending-invoice';
import { MyProPage } from 'src/pages/my-pros/my-pro/my-pro';
import { AboutThisPageService } from 'src/providers/about-this-page/about-this-page.service';
import { OnboardingProvider } from 'src/providers/onboarding/onboarding.provider';
import { OnboardingDummyDataService } from 'src/shared/services/onboarding-dummy-data.service';
import { DemoBlockService } from 'src/shared/services/demo-block.service';
import { InfiniteScrollCustomEvent } from '@ionic/core';
import { TidyUiComponentsModule} from 'src/tidy-ui-components/tidy-ui-components.module';

@Component({
  selector: 'bills-content',
  templateUrl: './bills-content.component.html',
  styleUrls: ['./bills-content.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    IonicModule,
    MatCheckboxModule,
    MatMenuModule,
    MatIconModule,
    TidyUiComponentsModule,
    SharedModule,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class BillsContentComponent implements OnInit, OnDestroy {
  private destroyRef = inject(DestroyRef);
  private client = inject(Client);
  private invoices = inject(Invoices);
  private navCtrl = inject(CustomNavController);
  private pros = inject(Pros);
  private rightSidePanelService = inject(RightSidePanelService);
  private storage = inject(TidyStorage);
  private fb = inject(FormBuilder);
  private util = inject(Util);
  private onboardingDummyDataService = inject(OnboardingDummyDataService);
  private readonly demoBlock = inject(DemoBlockService);
  private aboutThisPageService = inject(AboutThisPageService);
  private onboardingProvider = inject(OnboardingProvider);
  readonly chatDockService = inject(ChatDockService);
  readonly windowService = inject(WindowService);
  private cdr = inject(ChangeDetectorRef);

  addressResponse: any;
  addressFilter: any;
  bills: any = [];
  errorMessage: string;
  isGettingFilteredBills = false;
  private lastBillsFetchTime = 0;
  private lastBillsFetchKey = '';
  private readonly BILLS_CACHE_DURATION_MS = 2000;
  showLoadingSpinner: boolean;
  statusItems = [
    { viewValue: 'All', value: 'all' },
    { viewValue: 'Pending', value: 'pending' },
    { viewValue: 'Paid', value: 'paid' }
  ];
  headers: any;
  rows: any;
  currentRows: any;
  form: UntypedFormGroup;
  dateRangeForm: UntypedFormGroup;
  loaded: boolean;
  pageSize: any;
  pageIndex: any;
  proList: any[] = [];
  routeReused: boolean;
  selectOptions: any;
  totalRows: any;
  filters: Partial<InvoiceFilterParams> = {};
  isDataLoaded = false;
  @ViewChild('tidyTable') tidyTable: any;
  @ViewChild('filterSearchInput') filterSearchInput: ElementRef<HTMLInputElement>;
  wasTableLoaded: boolean;

  // Filter state
  chips: ChipFilter[] = [{
    displayParentName: 'Status',
    parentName: 'status',
    name: 'Pending',
    value: 'pending',
    icon: 'flag',
  }];
  selectedFilters: {
    status: string[];
    pro: number[];
    propertyName: number[];
  } = {
    status: ['pending'],
    pro: [],
    propertyName: []
  };

  // Filter tree data
  filterTreeData: any[] = [];
  // filterTreeData narrowed by the dropdown search box. Categories are shallow
  // copies but the child objects are shared, so updateFilterTreeState() mutating
  // `checked` on filterTreeData is reflected here without a rebuild.
  visibleFilterTreeData: any[] = [];
  filterSearch = '';
  showAboutThisPage: boolean;
  overlayTop: string;
  private _billsHeader: ElementRef;
  private overlayResizeObserver: ResizeObserver;
  @ViewChild('billsHeader', { read: ElementRef }) set billsHeader(el: ElementRef) {
    this._billsHeader = el;
    if (el && this.showAboutThisPage && !this.overlayResizeObserver) {
      this.setupOverlayObserver();
    }
  }

  constructor() {
    this.form = this.fb.group({
      selectedStatus: ['all'],
    });
    this.dateRangeForm = this.fb.group({
      start: '',
      end: '',
      customStringSelect: ''
    });
    this.selectOptions = [
      { viewValue: 'Custom', value: 'custom' },
      { viewValue: 'Today', value: 'today' },
      { viewValue: 'Last Week', value: 'last_week' },
      { viewValue: 'Last Month', value: 'last_month' },
      { viewValue: 'All Time', value: 'all_time' }
    ];
    this.showAboutThisPage = this.aboutThisPageService.checkIfShouldShowAboutThisPage('bills');
  }

  async ngOnInit() {
    try {
      await this.loadPage();
      this.openBillPage();
    } catch (err) {
      this.util.showError((err.error && err.error.message) ? err.error.message : err.message, 10000);
    }
  }

  async loadPage(showLoadingSpinner = false, setStoredRoute = false) {
    if (showLoadingSpinner) {
      this.showLoadingSpinner = true;
    }
    this.showAboutThisPage = this.aboutThisPageService.checkIfShouldShowAboutThisPage('bills');
    this.bills = [];
    await this.getPageDataNoLoadingIndicator();
    this.showLoadingSpinner = false;
    this.cdr.markForCheck();
    if (this.windowService.isDesktopRes && setStoredRoute) {
      this.storage.setStoredRoute('/bills').then(async () => {
        this.bills = [];
        this.routeReused = true;
        await this.loadPage(true);
      });
    }
  }

  private setupOverlayObserver() {
    const updateTop = () => {
      if (!this._billsHeader) {return;}
      const top = this._billsHeader.nativeElement.getBoundingClientRect().top - 8;
      this.overlayTop = `${top  }px`;
      this.cdr.markForCheck();
    };
    this.overlayResizeObserver = new ResizeObserver(() => updateTop());
    this.overlayResizeObserver.observe(document.body);
    setTimeout(() => updateTop());
  }

  ngOnDestroy() {
    this.overlayResizeObserver?.disconnect();
  }

  dismissAboutThisPage() {
    this.showAboutThisPage = false;
    this.overlayResizeObserver?.disconnect();
    this.bills = [];
    this.currentRows = [];
    this.rows = [];
    this.lastBillsFetchKey = '';
    this.lastBillsFetchTime = 0;
    this.cdr.markForCheck();
    this.loadPage(true, false);
  }

  async openBillPage() {
    if (this.showAboutThisPage) {
      return;
    }
    const shouldOpenBillPageID = await this.storage.retrieve('shouldOpenBillPageID');
    if (shouldOpenBillPageID) {
      let bill = this.bills.find((item) => item.id === shouldOpenBillPageID);
      if (!bill) {
        bill = await this.invoices.getInvoiceById(shouldOpenBillPageID);
      }
      this.goToInvoicePage(bill);
      await this.storage.delete('shouldOpenBillPageID')
    }
  }

  async getPageDataNoLoadingIndicator() {
    return this.loadPageData(false);
  }

  @Loading('', true)
  async getPageData() {
    return this.loadPageData(true);
  }

  private async loadPageData(withLoadingIndicator: boolean) {
    if (!withLoadingIndicator && !this.routeReused) {
      this.isDataLoaded = false;
    }

    this.chips = [{
      displayParentName: 'Status',
      parentName: 'status',
      name: 'Pending',
      value: 'pending',
      icon: 'flag',
    }];
    this.selectedFilters = {
      status: ['pending'],
      pro: [],
      propertyName: []
    };
    this.filters = {
      status: ['pending'],
      team_ids: null,
      jobs_since_date: null,
      jobs_until_date: null,
      address_ids: null
    };

    const savedAddressFilter = localStorage.getItem('billsAddressFilter');
    if (savedAddressFilter) {
      const addressIds = JSON.parse(savedAddressFilter).map((id: any) => Number(id));
      this.filters.address_ids = addressIds;
      this.selectedFilters.propertyName = addressIds;
      localStorage.removeItem('billsAddressFilter');
      localStorage.setItem('isAllAddressesSelected', 'false');
    } else {
      localStorage.setItem('isAllAddressesSelected', 'true');
    }

    this.pageSize = 25;
    this.pageIndex = 1;
    this.form.reset();
    this.buildHeaders();
    await this.getInitialData();

    setTimeout(() => {
      if (this.tidyTable?.paginator) {
        this.tidyTable.paginator.length = this.totalRows;
      }
    }, withLoadingIndicator ? 500 : 1000);

    this.loaded = true;
    this.cdr.markForCheck();
  }

  async getInitialData(): Promise<void> {
    try {
      const prosPromise = this.pros.getAllPros();
      const addressDataPromise = this.getAddressData();
      const billsPromise = this.getFilteredBills(this.filters, false);
      this.form.patchValue({
        selectedStatus: 'pending'
      })
      const [pros] = await Promise.all([prosPromise, addressDataPromise, billsPromise]);
      this.proList = this.parseProList(pros);
      this.buildFilterTreeData();
      this.applySavedPropertyFilter();
      this.isDataLoaded = true;
      this.cdr.markForCheck();
    } catch (err) {
      this.util.showError((err.error && err.error.message) ? err.error.message : err.message, 10000);
    }
  }

  parseProList(pros: any[]): any[] {
    return pros.map((pro) => ({
        value: pro.id,
        viewValue: pro?.name ? new TidyAbbreviateLastName().transform(pro.name) : '',
        teamId: pro?.main_team_id,
      }));
  }

  sortBillsByJobDate(bills: any[]): any[] {
    return bills.sort((a, b) => {
      const dateA = a.items?.[0]?.service_details?.date;
      const dateB = b.items?.[0]?.service_details?.date;
      if (!dateA && !dateB) {
        return 0;
      }
      if (!dateA) {
        return 1;
      }
      if (!dateB) {
        return -1;
      }
      return new Date(dateB).getTime() - new Date(dateA).getTime();
    });
  }

  buildRows(bills: any[]): void {
    this.rows = [];
    bills.map((item) => {
      const serviceDetails = item.items?.[0]?.service_details;
      const alreadyAdded = this.rows.find((row) => row.id === item.id);
      if (alreadyAdded) {
        const alreadyAddedIndex = this.rows.findIndex((row) => row.id === item.id);
        this.rows.splice(alreadyAddedIndex, 1);
      }
      this.rows.push({
        id: item.id,
        'Property': {
          value: this.getPropertyName(serviceDetails?.address) || '',
          action: this.goToProperty.bind(this, serviceDetails?.address?.id),
          color: this.addressResponse ? (this.addressResponse.find((address) => address.id === serviceDetails?.address?.id)?.color || '#1E2125') : '#1E2125',
          addressId: serviceDetails?.address?.id
        },
        'Pro': {
          value: new TidyAbbreviateLastName().transform(getSellerDisplay(item).name),
          action: this.goToPro.bind(this, getSellerDisplay(item).teamId, serviceDetails?.address?.id),
        },
        'Status': {
          value: item.full_paid ? 'Paid' : 'Pending',
          action: null,
        },
        'Job': {
          value: serviceDetails?.name || '',
          action: null,
        },
        'Job Date': {
          value: new ParseDateTimePipe().transform(serviceDetails?.date, 'ddd MMM d'),
          action: null,
        },
        'Total Amount': {
          value: new TidyCurrencyPipe().transform(item.amount),
          action: null,
        },
        'last-column': {
          value: '',
          action: null,
        },
      });
    });
    this.currentRows = this.rows;
  }

  goToPro(id: number, addressId: number): void {
    if (this.showAboutThisPage) {
      return;
    }
    if (this.demoBlock.block()) { return; }
    const params = {
      addressId,
      proId: id
    };
    this.goToPage(`my-pro/${id}`, params, MyProPage);
  }

  goToProperty(id: number): void {
    if (this.showAboutThisPage) {
      return;
    }
    this.navCtrl.navigateForward(`properties?propertyId=${id}`);
  }

  getPropertyName(address: any): string {
    if (!address) {
      return '';
    }
    const name = address.address_name || address.address || address.city || address.state;
    if (!name) {
      return '';
    }
    return address.zip ? `${name}, ${address.zip}` : name;
  }

  buildHeaders(): void {
    this.headers = [
      'Property',
      'Pro',
      'Status',
      'Job',
      'Job Date',
      'Total Amount',
      'last-column'
    ];
  }

  sortChanged(sort) {
    if (this.showAboutThisPage) {
      return;
    }
    this.rows.sort((a, b) => {
      if (!a[sort.active]?.value || !b[sort.active]?.value) {
        return 0;
      }

      if (sort.active === 'Job Date') {
        const parseDate = (dateStr: string) => {
          const currentYear = LuxonDateTime.local().year;
          if (!dateStr.includes(',')) {
            return LuxonDateTime.fromFormat(`${dateStr  }, ${  currentYear}`, 'EEE MMM d, yyyy');
          }
          return LuxonDateTime.fromFormat(dateStr, 'EEE MMM d, yyyy');
        };

        try {
          const dateA = parseDate(a[sort.active].value).toJSDate().getTime();
          const dateB = parseDate(b[sort.active].value).toJSDate().getTime();
          return sort.direction === 'asc' ? dateA - dateB : dateB - dateA;
        } catch {
          return 0;
        }
      } else {
        const valueA = this.parseRowNumberValue(a[sort.active].value);
        const valueB = this.parseRowNumberValue(b[sort.active].value);

        if (typeof valueA === 'number' && typeof valueB === 'number') {
          return sort.direction === 'asc' || sort.direction === '' ? valueA - valueB : valueB - valueA;
        } else {
          const strA = String(valueA || '').toLowerCase();
          const strB = String(valueB || '').toLowerCase();
          if (strA < strB) {return sort.direction === 'asc' || sort.direction === '' ? -1 : 1;}
          if (strA > strB) {return sort.direction === 'asc' || sort.direction === '' ? 1 : -1;}
          return 0;
        }
      }
    });
    this.currentRows = this.rows;
  }

  parseRowNumberValue(value: string): string | number {
    if (value.includes('$')) {
      const numberValue = Number(value.replace(/\$|\.|\s/g, ''));
      return isNaN(numberValue) ? value : numberValue;
    }
    return value;
  }

  resetDefaultChips(params?: Partial<InvoiceFilterParams>) {
    if (params?.status?.includes('pending')) {
      const hasPendingStatusChip = this.chips?.find((chip) => chip.value === 'pending');
      if (!hasPendingStatusChip) {
        this.addChip({
          displayParentName: 'Status',
          parentName: 'status',
          name: 'pending',
          value: 'pending'
        });
        this.updateFilterTreeState();
      }
    }
    const hasOnlyPendingStatus = params?.status?.length === 1 && params?.status?.includes('pending');
    if (!params?.address_ids && !params?.team_ids && hasOnlyPendingStatus) {
      const notKeeperChips = this.chips?.filter((chip) => chip.value !== 'pending' && chip.parentName !== 'pro' && chip.parentName !== 'propertyName');
      notKeeperChips?.forEach((chip) => {
        this.selectedFilters[chip.parentName] = this.selectedFilters[chip.parentName].filter((value) => value !== chip.value);
        this.chips = this.chips.filter((item) => !(item.parentName === chip.parentName && item.value === chip.value));
        this.updateFilterTreeState();
      });
      const propertyChips = this.chips?.filter((chip) => chip.parentName === 'propertyName')?.map((chip) => Number(chip.value)) || [];
      const proChips = this.chips?.filter((chip) => chip.parentName === 'pro')?.map((chip) => Number(chip.value)) || [];
      const foundPros = this.proList.filter((pro) => proChips.includes(pro.value));

      params.address_ids = propertyChips.length > 0 ? propertyChips : null;
      params.team_ids = foundPros.length > 0 ? foundPros.map((pro) => pro.teamId) : null;
    }
  }

  async getFilteredBills(params?: Partial<InvoiceFilterParams>, showLoadingSpinner = true): Promise<void> {
    const now = Date.now();
    const fetchKey = JSON.stringify({ params, pageSize: this.pageSize, pageIndex: this.pageIndex });
    if (fetchKey === this.lastBillsFetchKey && (now - this.lastBillsFetchTime) < this.BILLS_CACHE_DURATION_MS) {
      return;
    }
    this.lastBillsFetchTime = now;
    this.lastBillsFetchKey = fetchKey;
    this.resetDefaultChips(params);
    let loading = null;
    if (this.loaded && !this.routeReused && showLoadingSpinner) {
      this.pageIndex = 1;
      loading = await this.util.showLoading();
    }
    const billsMockData = this.onboardingDummyDataService.getBillsMockData();
    const bills = this.showAboutThisPage ? {body: billsMockData, totalRecords: billsMockData.length} : await this.invoices.getAllInvoices(params, this.pageSize, this.pageIndex);
    this.totalRows = Number(bills.totalRecords) || 100;
    if (showLoadingSpinner) {
      this.bills = this.sortBillsByJobDate([...bills.body]);
    } else {
      this.bills = this.sortBillsByJobDate([...this.bills, ...bills.body]);
    }
    const paidInvoiceId = await this.storage.retrieve('paidInvoiceId');
    if (paidInvoiceId && params.status.includes('pending')) {
      this.bills = this.bills.filter((bill) => bill.id !== paidInvoiceId);
      await this.storage.delete('paidInvoiceId');
    }
    this.buildRows(this.bills);
    this.cdr.markForCheck();
    if (this.tidyTable) {
      setTimeout(() => {
        if (this.tidyTable?.paginator) {
          this.tidyTable.paginator.length = this.totalRows;
          this.tidyTable.paginator.pageIndex = this.pageIndex - 1;
        }
      }, 500);
    }
    loading?.dismiss();
  }

  onFilterValueChange(event: number[] | string, type: string): void {
    if (this.showAboutThisPage) {
      return;
    }
    this.pageIndex = 1;
    if (type === 'pro') {
      const proIds = event as number[];
      const foundPros = this.proList.filter((pro) => proIds.includes(pro.value));
      this.filters['team_ids'] = foundPros.length > 0 ? foundPros.map((pro) => pro.teamId) : null;
    }
    if (type === 'status') {
      const status = event as string;
      this.filters['status'] = status !== 'all' ? [status] : null;
    }
    if (type === 'address') {
      const addressIds = event as number[];
      this.filters['address_ids'] = addressIds.length > 0 ? addressIds : null;
    }
    this.getFilteredBills(this.filters);
  }

  getEmptyStateMessage(): string {
    const statusChip = this.chips?.find(c => c.parentName === 'status');
    if (statusChip && statusChip.value !== 'all') {
      return `No ${statusChip.name.toLowerCase()} bills`;
    }

    return 'No bills found';
  }

  selectCustomDateRange(selection) {
    if (this.showAboutThisPage) {
      return;
    }
    const start = new Date(selection.start);
    const end = new Date(selection.end);
    const startDate = LuxonDateTime.fromJSDate(start).toISODate();
    const endDate = LuxonDateTime.fromJSDate(end).toISODate();
    this.filters['jobs_since_date'] = startDate;
    this.filters['jobs_until_date'] = endDate;
    this.getFilteredBills(this.filters);
  }

  selectStringDateRange(selection) {
    if (this.showAboutThisPage) {
      return;
    }
    const today = LuxonDateTime.local();
    let startDate = today.toISODate();
    let endDate = today.toISODate();
    if (selection === 'last_week') {
      startDate = today.minus({ days: 7 }).toISODate();
    }
    if (selection === 'last_month') {
      startDate = today.minus({ months: 1 }).toISODate();
    }
    if (selection === 'all_time') {
      startDate = null;
      endDate = null;
    }
    this.filters['jobs_since_date'] = startDate;
    this.filters['jobs_until_date'] = endDate;
    this.getFilteredBills(this.filters);
  }

  onTableColumnClick(event) {
    if (this.showAboutThisPage) {
      return;
    }
    if (this.demoBlock.block()) { return; }
    const foundBill = this.bills.find((bill) => bill.id === event.element.id);
    this.goToInvoicePage(foundBill);
  }

  async getAddressData() {
    this.addressFilter = await this.navCtrl.getParam('addressFilter') || await this.buildAddressFilter();
  }

  async buildAddressFilter() {
    this.addressResponse = await this.client.getAddresses();
    return this.client.parseAddressList(this.addressResponse, false);
  }

  async goToInvoicePage(invoice) {
    if (this.demoBlock.block()) { return; }
    try {
      const seller = getSellerDisplay(invoice);
      const params: any = {
        invoiceId: invoice.id,
        hasAccount: true,
        proName: seller.name,
        proEmail: seller.email,
        proPhone: seller.phone,
        proTeamId: seller.teamId,
        proId: invoice?.seller?.data?.team_owner?.id ?? invoice?.homekeeper_data?.id,
        isLoggedIntoClientApp: true,
        successRoute: 'bills',
        invoice,
      }
      const path = invoice.full_paid ? `paid-bill/${invoice.id}` : `pending-bill/${invoice.id}`;
      const component = invoice.full_paid ? PaidInvoicePage : PendingInvoicePage;
      this.goToPage(path, params, component);
    } catch (err) {
      this.util.showError((err.error && err.error.message) ? err.error.message : err.message, 10000);
    }
  }

  async goToPage(url, params, component) {
    if (this.showAboutThisPage) {
      return;
    }
    const isMobileResolution = !this.windowService.isDesktopRes;
    if (!isMobileResolution) {
      const isRightSidePanelAlreadyOpen = await this.storage.retrieve('dialog-right-side-open');
      if (isRightSidePanelAlreadyOpen) {
        this.rightSidePanelService.navigateTo(url, params, component);
      } else {
        this.storage.save('dialog-right-side-open', true);
        this.storage.save('dialog-params', params);
        this.rightSidePanelService.openRightSidePanel(component);
      }
      this.rightSidePanelService.panelClosed().pipe(take(1), takeUntilDestroyed(this.destroyRef)).subscribe(async () => {
        const didMakeBillChanges = await this.storage.retrieve('didMakeBillChanges');
        if (didMakeBillChanges) {
          this.bills = [];
          await this.storage.save('didMakeBillChanges', false);
          this.getPageData();
        }
      });
    } else {
      this.navCtrl.navigateForward(url, params);
    }
  }

  goToBillSettingsPage() {
    if (this.showAboutThisPage) {
      return;
    }
    if (this.demoBlock.block()) { return; }
    this.goToPage('bill-settings', {}, BillSettingsPage);
  }

  async handleInfiniteScroll(infiniteScrollEvent: InfiniteScrollCustomEvent) {
    if (this.bills.length >= this.totalRows) {
      infiniteScrollEvent?.target?.complete();
      return;
    }
    this.pageIndex += 1;
    await this.getFilteredBills(this.filters, false);
    if (infiniteScrollEvent?.target) {
      infiniteScrollEvent.target.complete();
    }
  }

  async pageChange() {
    if (this.showAboutThisPage) {
      return;
    }
    if (this.isGettingFilteredBills) {
      return;
    }
    if (this.bills.length >= this.totalRows) {
      return;
    }
    this.isGettingFilteredBills = true;
    if (this.tidyTable) {
      this.tidyTable.isLoading = true;
    }
    this.pageIndex += 1;
    try {
      await this.getFilteredBills(this.filters, false);
    } finally {
      if (this.tidyTable) {
        this.tidyTable.isLoading = false;
      }
      this.isGettingFilteredBills = false;
    }
  }

  buildFilterTreeData(): void {
    const treeData = [
      {
        name: 'Status',
        parentName: 'status',
        children: this.statusItems.map(status => ({
          name: status.viewValue,
          value: status.value.toString(),
          checked: status.value === 'pending',
        }))
      }
    ];
    if (this.proList.length > 0) {
      treeData.push({
        name: 'Pro',
        parentName: 'pro',
        children: this.proList.map(pro => ({
          name: pro.viewValue,
          value: pro.value,
          checked: false,
        }))
      });
    }
    if (this.addressFilter.length > 0) {
      treeData.push({
        name: 'Property',
        parentName: 'propertyName',
        children: this.addressFilter.map(address => ({
          name: address.viewValue,
          value: address.value,
          checked: false,
        }))
      });
    }
    this.filterTreeData = treeData;
    this.applyFilterSearch();
  }

  // Mat-menu moves focus to its first item on open, so hand it to the search box
  // afterwards. Desktop only — on mobile this would pop the keyboard open just
  // for tapping the filter icon.
  onFilterMenuOpened(): void {
    if (!this.windowService.isDesktopRes) {
      return;
    }
    setTimeout(() => this.filterSearchInput?.nativeElement.focus());
  }

  onFilterSearch(event: Event): void {
    this.filterSearch = (event.target as HTMLInputElement).value;
    this.applyFilterSearch();
    this.cdr.markForCheck();
  }

  // The menu owns keyboard focus while open, so keystrokes have to be kept from
  // reaching its typeahead/arrow handling. Escape and Tab still bubble so the
  // menu can close and focus can move on.
  onFilterSearchKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape' || event.key === 'Tab') {
      return;
    }
    event.stopPropagation();
  }

  clearFilterSearch(refocus = false): void {
    this.filterSearch = '';
    this.applyFilterSearch();
    if (refocus) {
      this.filterSearchInput?.nativeElement.focus();
    }
    this.cdr.markForCheck();
  }

  private applyFilterSearch(): void {
    const search = this.filterSearch.trim().toLowerCase();
    if (!search) {
      this.visibleFilterTreeData = this.filterTreeData;
      return;
    }
    const matches: any[] = [];
    this.filterTreeData.forEach((category) => {
      if (category.name.toLowerCase().includes(search)) {
        matches.push(category);
        return;
      }
      const children = category.children.filter((child) => child.name?.toLowerCase().includes(search));
      if (children.length > 0) {
        matches.push({ ...category, children });
      }
    });
    this.visibleFilterTreeData = matches;
  }

  getFilterCategory(categoryName: string): any {
    return this.filterTreeData.find(cat => cat.name === categoryName);
  }

  toggleFilter(categoryName: string, itemValue: any, event: MatCheckboxChange): void {
    const category = this.getFilterCategory(categoryName);
    if (!category) {
      return;
    }
    const item = category.children.find(child => child.value === itemValue);
    if (item) {
      item.checked = event.checked;
    }
    const chip: ChipFilter = {
      displayParentName: categoryName,
      parentName: category.parentName,
      name: item?.name || itemValue,
      value: itemValue,
      icon: this.getChipIcon(category.parentName),
      color: this.getChipColor(category.parentName, itemValue),
    };
    if (event.checked) {
      this.addChip(chip);
    } else {
      this.removeChip(chip);
    }
  }

  private getChipIcon(parentName: string): string {
    switch (parentName) {
      case 'propertyName':
        return 'home';
      case 'status':
        return 'flag';
      case 'pro':
        return 'person';
      default:
        return '';
    }
  }

  private getChipColor(parentName: string, value: any): string {
    if (parentName === 'propertyName') {
      const address = this.addressResponse?.find((a) => a.id === Number(value));
      return address?.color || '#1E2125';
    }
    return '';
  }

  addChip(chip: ChipFilter): void {
    if (this.showAboutThisPage) {
      return;
    }
    if (chip.parentName === 'status') {
      this.selectedFilters.status = [chip.value.toString()];
      this.chips = this.chips.filter(c => c.parentName !== 'status');
    } else {
      this.selectedFilters[chip.parentName].push(Number(chip.value));
    }
    this.chips.push(chip);
    if (chip.parentName === 'pro') {
      this.onFilterValueChange(this.selectedFilters.pro, 'pro');
    } else if (chip.parentName === 'status') {
      this.onFilterValueChange(chip.value.toString(), 'status');
    } else if (chip.parentName === 'propertyName') {
      this.onFilterValueChange(this.selectedFilters.propertyName, 'address');
    }
    this.updateFilterTreeState();
  }

  updateFilterTreeState(): void {
    this.filterTreeData.forEach((category) => {
      category.children.forEach((child) => {
        if (category.parentName === 'status') {
          child.checked = this.selectedFilters.status.includes(child.value.toString());
        } else if (this.selectedFilters[category.parentName]?.includes(Number(child.value))) {
          child.checked = true;
        } else {
          child.checked = false;
        }
      });
    });
  }

  removeChip(chip: ChipFilter): void {
    if (this.showAboutThisPage) {
      return;
    }
    this.chips = this.chips.filter(
      (item) => !(item.parentName === chip.parentName && item.value === chip.value)
    );
    if (chip.parentName === 'status') {
      if (chip.value === 'all') {
        this.util.showInfo('You cannot remove the all status filter, but you can remove the other filters', 5000);
        return;
      }
      this.chips.push({
        displayParentName: 'Status',
        parentName: 'status',
        name: 'all',
        value: 'all',
        icon: 'flag',
      });
      this.selectedFilters.status = ['all'];
      this.onFilterValueChange('all', 'status');
    } else {
      this.selectedFilters[chip.parentName] = this.selectedFilters[chip.parentName].filter((value) => value !== chip.value);
      if (chip.parentName === 'pro') {
        this.onFilterValueChange(this.selectedFilters.pro, 'pro');
      } else if (chip.parentName === 'propertyName') {
        this.onFilterValueChange(this.selectedFilters.propertyName, 'address');
      }
    }
    this.updateFilterTreeState();
  }

  async exportCSV() {
    if (this.showAboutThisPage) {
      return;
    }
    if (!this.tidyTable) {
      this.util.showError('No data to export', 5000);
      return;
    }
    try {
      const loadingIndicator = await this.util.showLoading({message: 'Preparing export...'});
      const allBillsMockData = this.onboardingDummyDataService.getBillsMockData();
      const allBills = this.showAboutThisPage ?
        {body: allBillsMockData, totalRecords: allBillsMockData.length} :
        await this.invoices.getAllInvoicesPaginated(this.filters);
      const allBillsData = this.sortBillsByJobDate([...allBills.body]);
      const tempRows = [];
      allBillsData.forEach((item) => {
        const serviceDetails = item.items?.[0]?.service_details;
        tempRows.push({
          id: item.id,
          'Property': {
            value: this.getPropertyName(serviceDetails?.address) || '',
            action: this.goToProperty.bind(this, serviceDetails?.address?.id),
            color: this.addressResponse ? (this.addressResponse.find((address) => address.id === serviceDetails?.address?.id)?.color || '#1E2125') : '#1E2125',
            addressId: serviceDetails?.address?.id
          },
          'Pro': {
            value: new TidyAbbreviateLastName().transform(getSellerDisplay(item).name),
            action: this.goToPro.bind(this, getSellerDisplay(item).teamId, serviceDetails?.address?.id),
          },
          'Status': {
            value: item.full_paid ? 'Paid' : 'Pending',
            action: null,
          },
          'Job': {
            value: serviceDetails?.name || '',
            action: null,
          },
          'Job Date': {
            value: serviceDetails?.date ? LuxonDateTime.fromISO(serviceDetails.date).toFormat('yyyy-MM-dd') : new ParseDateTimePipe().transform(serviceDetails?.date, 'ddd MMM d'),
            action: null,
          },
          'Total Amount': {
            value: new TidyCurrencyPipe().transform(item.amount),
            action: null,
          },
          'last-column': {
            value: '',
            action: null,
          },
        });
      });
      const exportDataSource = new MatTableDataSource(tempRows);
      this.tidyTable.exportCsv(exportDataSource);
      loadingIndicator?.dismiss();
    } catch (err) {
      this.util.showError((err.error && err.error.message) ? err.error.message : err.message, 10000);
    }
  }

  private applySavedPropertyFilter(): void {
    if (!this.selectedFilters.propertyName || this.selectedFilters.propertyName.length === 0) {
      return;
    }
    this.selectedFilters.propertyName.forEach(addressId => {
      const numericId = Number(addressId);
      const foundAddress = this.addressFilter.find(addr => Number(addr.value) === numericId);
      const existingChip = this.chips.find(c => c.parentName === 'propertyName' && Number(c.value) === numericId);
      if (foundAddress && !existingChip) {
        const addressObj = this.addressResponse?.find((a) => a.id === numericId);
        const chip: ChipFilter = {
          displayParentName: 'Property',
          parentName: 'propertyName',
          name: foundAddress.viewValue,
          value: numericId,
          icon: 'home',
          color: addressObj?.color || '#1E2125',
        };
        this.chips.push(chip);
      }
    });
    this.updateFilterTreeState();
  }
}
